import {
	CheckCircle as CheckCircleIcon,
	ClearAll as ClearAllIcon,
	CloudDownload as CloudDownloadIcon,
	Delete as DeleteIcon,
	Download as DownloadIcon,
	Error as ErrorIcon,
	Refresh as RefreshIcon
} from "@mui/icons-material";
import {
	Badge,
	Box,
	Button,
	Chip,
	CircularProgress,
	Divider,
	IconButton,
	LinearProgress,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Menu,
	Tooltip,
	Typography,
	useTheme
} from "@mui/material";
import { orderBy } from "lodash";
import moment from "moment";
import React, { FC, ReactNode, useMemo, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../redux";
import { clearCompletedJobs, removeDownloadJob, retryDownloadJob } from "../../redux/reducers/downloadJobs.reducer";
import { showFeedbackNotification } from "../../redux/reducers/feedbackNotification.reducer";
import downloadServices from "../../redux/services/download.services";
import { IDownloadJob } from "../../types";

const DownloadJobsDropdown: FC = () => {
	const dispatch = useAppDispatch();
	const theme = useTheme();

	const downloadJobs = useAppSelector((state) => state.downloadJobs.jobs);

	const anchorElementRef = useRef<HTMLButtonElement>(null);

	const [isOpen, setIsOpen] = useState(false);

	const activeJobs = useMemo<IDownloadJob[]>(
		() => downloadJobs.filter((job) => job.status === "downloading" || job.status === "pending"),
		[downloadJobs]
	);

	const completedJobs = useMemo<IDownloadJob[]>(
		() => downloadJobs.filter((job) => job.status === "completed" || job.status === "failed"),
		[downloadJobs]
	);

	function handleClick() {
		setIsOpen(true);
	}

	function handleClose() {
		setIsOpen(false);
	}

	function handleRemoveJob(jobId: string) {
		dispatch(removeDownloadJob(jobId));
	}

	function handleClearCompleted() {
		dispatch(clearCompletedJobs());
	}

	function handleDownloadFile(jobId: string) {
		downloadServices.handleDownloadReport(jobId).catch((error) => {
			dispatch(
				showFeedbackNotification({
					message: "data" in error && error.data ? String(error.data) : "Download failed",
					severity: "error"
				})
			);
		});
	}

	function handleRetryJob(jobId: string) {
		dispatch(retryDownloadJob(jobId));
		// The useDownloadJobsManager hook will automatically pick up the retry and reconnect to SSE
	}

	function getStatusIcon(status: string): ReactNode {
		switch (status) {
			case "downloading":
				return <CircularProgress size={16} />;
			case "completed":
				return <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />;
			case "failed":
				return <ErrorIcon color="error" sx={{ fontSize: 16 }} />;
			default:
				return <CloudDownloadIcon color="primary" sx={{ fontSize: 16 }} />;
		}
	}

	function getStatusColor(status: string): string {
		switch (status) {
			case "downloading":
				return theme.palette.primary.main;
			case "completed":
				return theme.palette.success.main;
			case "failed":
				return theme.palette.error.main;
			default:
				return theme.palette.grey[500];
		}
	}

	function getReportTypeDisplayName(reportType: string): string {
		switch (reportType) {
			case "logs":
				return "Event Logs";
			case "uptime":
				return "Uptime Report";
			case "authorized_vehicles":
				return "Authorized Vehicles";
			case "blacklist_vehicles":
				return "Blacklist Vehicles";
			default:
				return "Report";
		}
	}

	return (
		<>
			<Tooltip title="Downloads">
				<IconButton
					onClick={handleClick}
					ref={anchorElementRef}
					color="primary"
					size="small"
					sx={{
						position: "relative",
						backgroundColor: theme.palette.primary.light,
						"&:hover": {
							backgroundColor: theme.palette.primary.light
						}
					}}
				>
					<DownloadIcon />
					{activeJobs.length > 0 && (
						<Badge
							badgeContent={activeJobs.length}
							color="error"
							sx={{
								position: "absolute",
								top: 4,
								right: 4,
								"& .MuiBadge-badge": {
									fontSize: "0.65rem",
									minWidth: "18px",
									height: "18px",
									fontWeight: 600
								}
							}}
						/>
					)}
				</IconButton>
			</Tooltip>

			<Menu
				anchorEl={anchorElementRef.current}
				open={isOpen}
				onClose={handleClose}
				transformOrigin={{ horizontal: "right", vertical: "top" }}
				anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
				slotProps={{
					paper: {
						elevation: 1,
						sx: {
							width: 420,
							maxHeight: 500,
							mt: 1,
							borderRadius: "8px",
							border: `1px solid ${theme.palette.primary.light}`
						}
					}
				}}
			>
				{/* Header */}
				<Box
					sx={{
						p: 2,
						backgroundColor: theme.palette.primary.light,
						borderRadius: "8px 8px 0 0"
					}}
				>
					<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<DownloadIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
							<Typography
								variant="h6"
								sx={{
									fontWeight: 600,
									color: theme.palette.primary.main,
									fontSize: "1rem"
								}}
							>
								Downloads
							</Typography>
							{activeJobs.length > 0 && (
								<Chip
									label={activeJobs.length}
									size="small"
									sx={{
										backgroundColor: theme.palette.primary.main,
										color: "white",
										fontSize: "0.7rem",
										height: "20px",
										"& .MuiChip-label": {
											px: 1
										}
									}}
								/>
							)}
						</Box>
						{completedJobs.length > 0 && (
							<Button
								size="small"
								startIcon={<ClearAllIcon />}
								onClick={handleClearCompleted}
								sx={{
									minWidth: "auto",
									px: 1.5,
									py: 0.5,
									fontSize: "0.75rem",
									textTransform: "none",
									color: theme.palette.primary.main,
									"&:hover": {
										backgroundColor: theme.palette.primary.main,
										color: "white"
									}
								}}
							>
								Clear All
							</Button>
						)}
					</Box>
				</Box>

				{/* Content */}
				<Box sx={{ maxHeight: 400, overflow: "auto" }}>
					{downloadJobs.length === 0 ? (
						<Box
							sx={{
								p: 4,
								textAlign: "center",
								backgroundColor: theme.palette.grey[50]
							}}
						>
							<CloudDownloadIcon
								sx={{
									fontSize: 48,
									color: theme.palette.grey[400],
									mb: 1
								}}
							/>
							<Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
								No downloads
							</Typography>
							<Typography variant="caption" color="text.secondary">
								Start a download to see progress here
							</Typography>
						</Box>
					) : (
						<List sx={{ p: 0 }}>
							{orderBy(downloadJobs, "createdAt", "desc").map((job, index) => (
								<React.Fragment key={job.id}>
									<ListItem
										sx={{
											py: 2,
											px: 2,
											backgroundColor:
												job.status === "downloading" ? theme.palette.primary.light + "20" : "transparent",
											"&:hover": {
												backgroundColor: theme.palette.grey[50]
											}
										}}
									>
										<ListItemIcon sx={{ minWidth: 40, mr: 2 }}>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													width: 32,
													height: 32,
													borderRadius: "50%",
													backgroundColor: getStatusColor(job.status) + "20",
													border: `2px solid ${getStatusColor(job.status)}40`
												}}
											>
												{getStatusIcon(job.status)}
											</Box>
										</ListItemIcon>

										<ListItemText
											primary={
												<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
													<Box sx={{ flex: 1, minWidth: 0 }}>
														<Box
															sx={{
																fontWeight: 600,
																color: theme.palette.text.primary,
																mb: 0.5,
																fontSize: "0.875rem",
																lineHeight: 1.43
															}}
														>
															{getReportTypeDisplayName(job.reportType)}
														</Box>

														<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
															<Chip
																label={job.status.charAt(0).toUpperCase() + job.status.slice(1)}
																size="small"
																sx={{
																	backgroundColor: getStatusColor(job.status) + "20",
																	color: getStatusColor(job.status),
																	fontSize: "0.65rem",
																	height: "20px",
																	fontWeight: 500,
																	textTransform: "capitalize"
																}}
															/>
															<Box
																sx={{
																	fontSize: "0.75rem",
																	color: theme.palette.text.secondary,
																	lineHeight: 1.66
																}}
															>
																{moment(job.createdAt).fromNow()}
															</Box>
														</Box>
													</Box>

													<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1 }}>
														{job.status === "completed" && (
															<Tooltip title="Download File">
																<IconButton
																	size="small"
																	onClick={() => handleDownloadFile(job.id)}
																	sx={{
																		p: 0.5,
																		color: theme.palette.success.main,
																		"&:hover": {
																			backgroundColor: theme.palette.success.light
																		}
																	}}
																>
																	<DownloadIcon fontSize="small" />
																</IconButton>
															</Tooltip>
														)}
														{job.status === "failed" && (
															<Tooltip title="Retry Download">
																<IconButton
																	size="small"
																	onClick={() => handleRetryJob(job.id)}
																	sx={{
																		p: 0.5,
																		color: theme.palette.primary.main,
																		"&:hover": {
																			backgroundColor: theme.palette.primary.light
																		}
																	}}
																>
																	<RefreshIcon fontSize="small" />
																</IconButton>
															</Tooltip>
														)}
														<Tooltip title="Remove Job">
															<IconButton
																size="small"
																onClick={() => handleRemoveJob(job.id)}
																sx={{
																	p: 0.5,
																	color: theme.palette.error.main,
																	"&:hover": {
																		backgroundColor: theme.palette.error.light
																	}
																}}
															>
																<DeleteIcon fontSize="small" />
															</IconButton>
														</Tooltip>
													</Box>
												</Box>
											}
											secondary={
												<Box>
													{job.status === "downloading" && (
														<Box sx={{ mb: 1.5 }}>
															<Box
																sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}
															>
																<Box
																	sx={{
																		color: theme.palette.text.secondary,
																		fontWeight: 500,
																		fontSize: "0.75rem",
																		lineHeight: 1.66
																	}}
																>
																	Progress
																</Box>
																<Box
																	sx={{
																		color: getStatusColor(job.status),
																		fontWeight: 600,
																		fontSize: "0.75rem",
																		lineHeight: 1.66
																	}}
																>
																	{job.progress}%
																</Box>
															</Box>
															<LinearProgress
																variant="determinate"
																value={job.progress}
																sx={{
																	height: 6,
																	borderRadius: 3,
																	backgroundColor: theme.palette.grey[200],
																	"& .MuiLinearProgress-bar": {
																		borderRadius: 3,
																		backgroundColor: getStatusColor(job.status)
																	}
																}}
															/>
														</Box>
													)}

													{job.message && (
														<Box
															sx={{
																fontSize: "0.75rem",
																color: theme.palette.text.secondary,
																lineHeight: 1.66,
																mb: 0.5,
																display: "block"
															}}
														>
															{job.message}
														</Box>
													)}

													{job.error && (
														<Box
															sx={{
																p: 1,
																borderRadius: 1,
																backgroundColor: theme.palette.error.light,
																border: `1px solid ${theme.palette.error.main}20`
															}}
														>
															<Box
																sx={{
																	fontSize: "0.75rem",
																	color: theme.palette.error.main,
																	fontWeight: 500,
																	lineHeight: 1.66
																}}
															>
																{job.error}
															</Box>
														</Box>
													)}
												</Box>
											}
										/>
									</ListItem>
									{index < downloadJobs.length - 1 && (
										<Divider sx={{ mx: 2, backgroundColor: theme.palette.grey[200] }} />
									)}
								</React.Fragment>
							))}
						</List>
					)}
				</Box>
			</Menu>
		</>
	);
};

export default DownloadJobsDropdown;
